import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import axios from "axios";
import {
  Menu,
  X,
  Calculator,
  HelpCircle,
  AlertCircle,
  Globe,
  ChevronDown,
  Sun,
  Moon,
  LogIn
} from "lucide-react";
import LogoIndak from "../../assets/img/logo-pdf.png";
import ImageWarehouseRatesAI from "../../assets/img/warehouse_rates_ai.png";
import { getAutoTheme } from "../../utils/themeHelper";
import "../home/home.css";

const CITY_PRICES = {
  A: {
    labelEn: "Tarim / Husaisah / Sewun",
    labelId: "Tarim / Husaisah / Sewun",
    labelAr: "تريم / الحصيصة / سيئون",
    price: 90000
  },
  B: {
    labelEn: "Huraidhah / Mukallla / Syihir / Hami",
    labelId: "Huraidhah / Mukallla / Syihir / Hami",
    labelAr: "حريضة / المكلا / الشحر / الحامي",
    price: 95000
  },
  C: {
    labelEn: "Mahrah / Aden",
    labelId: "Mahrah / Aden",
    labelAr: "المهرة / عدن",
    price: 110000
  }
};

export default function CheckRates() {
  const navigate = useNavigate();
  const [lang, setLang] = useState(localStorage.getItem("lang") || "en");
  const [langOpen, setLangOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [theme, setTheme] = useState(getAutoTheme);
  const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";

  // Checker form states
  const [kgInput, setKgInput] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = lang;

    // Scroll Reveal Intersection Observer
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("revealed");
        }
      });
    }, { threshold: 0.1 });

    document.querySelectorAll(".reveal, .reveal-left, .reveal-right, .reveal-scale").forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [lang]);

  // Sync theme
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    localStorage.setItem("landing-theme", newTheme);
  };

  const changeLanguage = (newLang) => {
    setLang(newLang);
    localStorage.setItem("lang", newLang);
    setLangOpen(false);
  };

  const handleCalculate = async (e) => {
    e.preventDefault();
    const rawKg = Number(kgInput);
    const kg = Math.ceil(rawKg);

    if (!kg || kg <= 0) {
      toast.error(
        lang === "ar"
          ? "يرجى إدخال وزن صالح"
          : lang === "id"
          ? "Masukkan berat yang valid"
          : "Please enter a valid weight"
      );
      return;
    }
    if (!selectedCity || !CITY_PRICES[selectedCity]) {
      toast.error(
        lang === "ar"
          ? "يرجى اختيار مدينة الوجهة"
          : lang === "id"
          ? "Pilih kota tujuan"
          : "Please select a destination city"
      );
      return;
    }

    setLoading(true);

    const cityObj = CITY_PRICES[selectedCity];
    const pricePerKg = cityObj.price;
    const totalIDR = kg * pricePerKg;
    const cityLabel =
      lang === "ar"
        ? cityObj.labelAr
        : lang === "id"
        ? cityObj.labelId
        : cityObj.labelEn;

    try {
      const res = await axios.get("https://open.er-api.com/v6/latest/IDR", { timeout: 2500 });
      const usdRate = res.data?.rates?.USD || 0.0000625;
      const totalUSD = totalIDR * usdRate;

      setResults({
        kg,
        cityLabel,
        totalIDR,
        totalUSD,
        exchangeRate: Math.round(1 / usdRate),
        isLiveRate: true
      });
    } catch (err) {
      console.warn("Using offline USD conversion fallback rate");
      const fallbackRate = 0.0000625;
      const totalUSD = totalIDR * fallbackRate;

      setResults({
        kg,
        cityLabel,
        totalIDR,
        totalUSD,
        exchangeRate: Math.round(1 / fallbackRate),
        isLiveRate: false
      });
    } finally {
      setLoading(false);
    }
  };

  const text = {
    en: {
      navHome: "Home",
      navAbout: "About",
      navServices: "Services",
      navContact: "Contact",
      navCheckRates: "Check Rates",
      navSignIn: "Sign In",
      navDashboard: "Dashboard",
      badgeRates: "Check Shipping Rates",
      heroTitle: "Shipping Price Checker",
      heroSubtitle: "Calculate the estimated shipping cost of your cargo to Yemen or Saudi Arabia instantly based on shipment weight (Kg) and destination city.",
      cardTitle: "Calculate Estimated Shipping",
      cardDesc: "Enter the actual weight of the goods and the destination city.",
      labelKg: "Weight (Kilograms / Kg)",
      labelCity: "Select Destination City",
      placeholderCity: "-- Select Destination City --",
      btnCalculate: "Check Price Now",
      btnLoading: "Calculating...",
      emptyTitle: "No Calculations Yet",
      emptyDesc: "Fill in the weight and city form on the left, then click 'Check Price Now' to display the estimated shipping costs.",
      resultBadge: "Estimation Results",
      resultTitle: "Shipping Cost Details",
      resCity: "Destination City:",
      resWeight: "Calculated Weight:",
      resIDR: "Rupiah (IDR):",
      resUSD: "Dollar (USD):",
      resLiveRate: "Live currency exchange rate is applied automatically.",
      resOfflineRate: "Offline backup exchange rate is used.",
      footerDesc: "Trusted international logistics and remittance solutions from Indonesia to Yemen and Saudi Arabia.",
      footerCopy: "All rights reserved."
    },
    ar: {
      navHome: "الرئيسية",
      navAbout: "من نحن",
      navServices: "خدماتنا",
      navContact: "اتصل بنا",
      navCheckRates: "أسعار الشحن",
      navSignIn: "تسجيل الدخول",
      navDashboard: "لوحة التحكم",
      badgeRates: "حساب تكلفة الشحن",
      heroTitle: "حاسبة أسعار الشحن",
      heroSubtitle: "احسب تكلفة الشحن التقديرية لشحنتك إلى اليمن أو المملكة العربية السعودية فوراً بناءً على وزن الشحنة (كجم) ومدينة الوجهة.",
      cardTitle: "احسب تكلفة الشحن التقديرية",
      cardDesc: "أدخل الوزن الفعلي للبضائع ومدينة الوجهة.",
      labelKg: "الوزن (كيلو جرام / كجم)",
      labelCity: "اختر مدينة الوجهة",
      placeholderCity: "-- اختر مدينة الوجهة --",
      btnCalculate: "احسب السعر الآن",
      btnLoading: "جاري الحساب...",
      emptyTitle: "لا توجد حسابات بعد",
      emptyDesc: "املاً نموذج الوزن والمدينة على اليسار، ثم انقر على 'احسب السعر الآن' لعرض تكلفة الشحن التقديرية.",
      resultBadge: "النتائج التقديرية",
      resultTitle: "تفاصيل تكلفة الشحن",
      resCity: "المدينة الوجهة:",
      resWeight: "الوزن المحتسب:",
      resIDR: "روبية إندونيسية (IDR):",
      resUSD: "دولار أمريكي (USD):",
      resLiveRate: "يتم تطبيق سعر الصرف المباشر للعملة تلقائياً.",
      resOfflineRate: "يتم استخدام سعر الصرف الاحتياطي غير المباشر.",
      footerDesc: "حلول لوجستية وحوالات مالية دولية موثوقة من إندونيسيا إلى اليمن والمملكة العربية السعودية.",
      footerCopy: "جميع الحقوق محفوظة."
    },
    id: {
      navHome: "Beranda",
      navAbout: "Tentang Kami",
      navServices: "Layanan",
      navContact: "Kontak",
      navCheckRates: "Cek Ongkir",
      navSignIn: "Masuk",
      navDashboard: "Dashboard",
      badgeRates: "Kalkulator Ongkir",
      heroTitle: "Hitung Biaya Pengiriman",
      heroSubtitle: "Hitung perkiraan biaya kargo pengiriman Anda dari Indonesia ke Yaman atau Arab Saudi secara akurat dengan kalkulator online kami.",
      cardTitle: "Kalkulator Kargo",
      cardDesc: "Masukkan berat paket (kg) dan pilih kota tujuan di bawah.",
      labelKg: "Berat Pengiriman (KG)",
      labelCity: "Kota Tujuan Pengiriman",
      placeholderCity: "-- Pilih Kota Tujuan --",
      btnCalculate: "Hitung Sekarang",
      btnLoading: "Menghitung...",
      emptyTitle: "Belum Ada Perhitungan",
      emptyDesc: "Isi form berat paket dan kota tujuan di sebelah kiri, lalu klik 'Hitung Sekarang' untuk menampilkan perkiraan biaya pengiriman.",
      resultBadge: "Hasil Perkiraan",
      resultTitle: "Rincian Biaya Pengiriman",
      resCity: "Kota Tujuan:",
      resWeight: "Berat Paket:",
      resIDR: "Rupiah Indonesia (IDR):",
      resUSD: "Dolar AS (USD):",
      resLiveRate: "Menggunakan nilai tukar mata uang kurs langsung.",
      resOfflineRate: "Menggunakan estimasi nilai tukar cadangan.",
      footerDesc: "Solusi logistik dan transfer uang internasional terpercaya dari Indonesia ke Yaman dan Arab Saudi.",
      footerCopy: "Hak cipta dilindungi undang-undang."
    }
  };

  const t = text[lang] || text.en;

  return (
    <div className="landing-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* HEADER NAVBAR */}
      <header className="landing-header">
        <div className="header-container">
          <div className="header-logo" onClick={() => navigate("/")}>
            <img src={LogoIndak} alt="Indak Logo" className="logo-img" />
          </div>

          <nav className="desktop-nav">
            <button onClick={() => navigate("/")} className="nav-link">{t.navHome}</button>
            <button onClick={() => navigate("/about")} className="nav-link">{t.navAbout}</button>
            <button onClick={() => navigate("/services")} className="nav-link">{t.navServices}</button>
            <button onClick={() => navigate("/contact")} className="nav-link">{t.navContact}</button>
            <button onClick={() => navigate("/check-rates")} className="nav-link active-link-highlight">{t.navCheckRates}</button>

            {/* Language Switcher */}
            <div className="lang-dropdown-container">
              <button className="lang-dropdown-trigger" onClick={() => setLangOpen(!langOpen)}>
                <Globe size={16} />
                <span>{lang === "en" ? "EN" : lang === "id" ? "ID" : "AR"}</span>
                <ChevronDown size={14} />
              </button>
              {langOpen && (
                <div className="lang-dropdown-menu">
                  <button className={`lang-dropdown-item ${lang === "en" ? "active-lang" : ""}`} onClick={() => changeLanguage("en")}>
                    English
                  </button>
                  <button className={`lang-dropdown-item ${lang === "id" ? "active-lang" : ""}`} onClick={() => changeLanguage("id")}>
                    Bahasa Indonesia
                  </button>
                  <button className={`lang-dropdown-item ${lang === "ar" ? "active-lang" : ""}`} onClick={() => changeLanguage("ar")}>
                    العربية
                  </button>
                </div>
              )}
            </div>

            {/* Theme Toggle Button */}
            <button onClick={toggleTheme} className="theme-toggle-btn" title={theme === "light" ? "Switch to Dark Mode" : "Switch to Light Mode"}>
              {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
            </button>

            {/* Login Door Icon Button */}
            <button
              onClick={() => navigate(isLoggedIn ? "/admin/dashboard" : "/login")}
              className="nav-sig-button"
              title={isLoggedIn ? t.navDashboard : t.navSignIn}
            >
              <LogIn size={20} />
            </button>
          </nav>

          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="mobile-nav">
            <button onClick={() => navigate("/")} className="mobile-nav-link">{t.navHome}</button>
            <button onClick={() => navigate("/about")} className="mobile-nav-link">{t.navAbout}</button>
            <button onClick={() => navigate("/services")} className="mobile-nav-link">{t.navServices}</button>
            <button onClick={() => navigate("/contact")} className="mobile-nav-link">{t.navContact}</button>
            <button onClick={() => navigate("/check-rates")} className="mobile-nav-link">{t.navCheckRates}</button>

            <div style={{ display: "flex", gap: "20px", padding: "12px 0", alignItems: "center" }}>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={() => changeLanguage("en")}
                  style={{ background: "none", border: "none", color: lang === "en" ? "#c82333" : "#aaa", fontWeight: "700", cursor: "pointer" }}
                >
                  EN
                </button>
                <span style={{ color: "#444" }}>|</span>
                <button
                  onClick={() => changeLanguage("id")}
                  style={{ background: "none", border: "none", color: lang === "id" ? "#c82333" : "#aaa", fontWeight: "700", cursor: "pointer" }}
                >
                  ID
                </button>
                <span style={{ color: "#444" }}>|</span>
                <button
                  onClick={() => changeLanguage("ar")}
                  style={{ background: "none", border: "none", color: lang === "ar" ? "#c82333" : "#aaa", fontWeight: "700", cursor: "pointer" }}
                >
                  AR
                </button>
              </div>

              <button
                onClick={toggleTheme}
                style={{ background: "none", border: "none", color: "var(--header-link)", cursor: "pointer", padding: "4px" }}
              >
                {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
              </button>
            </div>

            <button onClick={() => navigate(isLoggedIn ? "/admin/dashboard" : "/login")} className="mobile-nav-sig-cta">
              <LogIn size={18} /> {isLoggedIn ? t.navDashboard : t.navSignIn}
            </button>
          </div>
        )}
      </header>

      {/* CHECK RATES HERO */}
      <section className="inner-hero-section">
        <div className="section-container">
          <div className="inner-hero-grid-contact">
            <div className="hero-content reveal-left">
              <span className="section-badge">{t.badgeRates}</span>
              <h1 className="hero-title">
                {lang === "ar" ? (
                  <>
                    حاسبة <span className="text-gradient">أسعار الشحن</span>
                  </>
                ) : (
                  <>
                    Shipping <span className="text-gradient">Price Checker</span>
                  </>
                )}
              </h1>
              <p className="hero-subtitle">
                {t.heroSubtitle}
              </p>
            </div>
            <div className="subpage-side-visual reveal-right">
              <img src={ImageWarehouseRatesAI} alt="Warehouse Scale Visual" />
            </div>
          </div>
        </div>
      </section>

      {/* PRICE CALCULATOR SECTION (With Motion Reveal) */}
      <section className="check-rates-main-section">
        <div className="section-container">
          <div className="checker-layout-grid">
            {/* Calculator Card */}
            <div className="calculator-form-card reveal-left">
              <div className="card-header">
                <h3><Calculator className="calc-icon" size={24} /> {t.cardTitle}</h3>
                <p>{t.cardDesc}</p>
              </div>

              <form onSubmit={handleCalculate} className="tracking-form">
                <div className="form-group">
                  <label htmlFor="kg">{t.labelKg}</label>
                  <input
                    type="number"
                    id="kg"
                    min="1"
                    step="any"
                    placeholder="Contoh: 5..."
                    value={kgInput}
                    onChange={(e) => setKgInput(e.target.value)}
                    className="input-custom-dark"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="city">{t.labelCity}</label>
                  <select
                    id="city"
                    value={selectedCity}
                    onChange={(e) => setSelectedCity(e.target.value)}
                    className="select-custom-dark"
                    required
                  >
                    <option value="">{t.placeholderCity}</option>
                    <option value="A">{lang === "ar" ? CITY_PRICES.A.labelAr : CITY_PRICES.A.labelEn}</option>
                    <option value="B">{lang === "ar" ? CITY_PRICES.B.labelAr : CITY_PRICES.B.labelEn}</option>
                    <option value="C">{lang === "ar" ? CITY_PRICES.C.labelAr : CITY_PRICES.C.labelEn}</option>
                  </select>
                </div>

                <button type="submit" className="btn-track-submit" disabled={loading}>
                  {loading ? t.btnLoading : t.btnCalculate}
                </button>
              </form>
            </div>

            {/* Results Display */}
            <div className="calculator-results-box reveal-right">
              {!results && !loading && (
                <div className="results-placeholder">
                  <HelpCircle size={48} className="text-muted-icon" />
                  <h4>{t.emptyTitle}</h4>
                  <p>{t.emptyDesc}</p>
                </div>
              )}

              {loading && (
                <div className="results-placeholder">
                  <div className="loader-ring"></div>
                  <p>{t.btnLoading}</p>
                </div>
              )}

              {results && !loading && (
                <div className="results-content-card">
                  <span className="section-badge">{t.resultBadge}</span>
                  <h3>{t.resultTitle}</h3>

                  <div className="result-metric-item">
                    <span className="metric-label">{t.resCity}</span>
                    <span className="metric-value">{results.cityLabel}</span>
                  </div>

                  <div className="result-metric-item">
                    <span className="metric-label">{t.resWeight}</span>
                    <span className="metric-value">{results.kg} Kg</span>
                  </div>

                  <hr className="divider-light" />

                  <div className="result-price-box">
                    <div className="price-row">
                      <span className="price-currency">{t.resIDR}</span>
                      <span className="price-value text-gradient">Rp {results.totalIDR.toLocaleString("id-ID")}</span>
                    </div>

                    <div className="price-row">
                      <span className="price-currency">{t.resUSD}</span>
                      <span className="price-value text-gradient-purple">${results.totalUSD.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="rate-source-disclaimer">
                    <AlertCircle size={14} />
                    <span>
                      {results.isLiveRate ? t.resLiveRate : t.resOfflineRate}
                      {" "}
                      (1 USD = Rp {results.exchangeRate.toLocaleString("id-ID")})
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="landing-footer">
        <div className="footer-container">
          <div className="footer-main-grid">
            <div className="footer-col footer-col-brand">
              <p className="footer-brand-desc">{t.footerDesc}</p>
            </div>

            <div className="footer-col footer-col-links">
              <h4 className="footer-col-title">{lang === "ar" ? "روابط سريعة" : lang === "id" ? "Navigasi Cepat" : "Quick Links"}</h4>
              <ul className="footer-links-list">
                <li><button onClick={() => navigate("/")}>{t.navHome}</button></li>
                <li><button onClick={() => navigate("/about")}>{t.navAbout}</button></li>
                <li><button onClick={() => navigate("/services")}>{t.navServices}</button></li>
                <li><button onClick={() => navigate("/check-rates")}>{t.navCheckRates}</button></li>
                <li><button onClick={() => navigate("/contact")}>{t.navContact}</button></li>
              </ul>
            </div>

            <div className="footer-col footer-col-services">
              <h4 className="footer-col-title">{lang === "ar" ? "وجهات الخدمة" : lang === "id" ? "Cakupan Destinasi" : "Service Coverage"}</h4>
              <ul className="footer-links-list text-muted-list">
                <li>🇾🇪 Yemen (Tarim, Mukalla, Aden, Sana'a)</li>
                <li>🇸🇦 Saudi Arabia (Jeddah, Riyadh, Makkah)</li>
                <li>📦 Air & Sea Cargo Shipping</li>
                <li>💵 Outbound & Inbound Money Transfer</li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p className="copyright-text">
              &copy; {new Date().getFullYear()} INDAK. {t.footerCopy}
            </p>
            {/* <div className="dev-tag">
              <span className="dev-by">{lang === "ar" ? "تطوير بواسطة" : "Dev By"}</span>
              <span className="dev-name">~ YallaBob ~</span>
            </div> */}
          </div>
        </div>
      </footer>
    </div>
  );
}
